import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import InfoPage from ".";

// Tags we override while the Summit page is mounted. The originals are
// restored on unmount so other routes keep their global meta from index.html.
export const SUMMIT_META_KEYS = [
  { attr: "name", key: "description" },
  { attr: "property", key: "og:title" },
  { attr: "property", key: "og:description" },
  { attr: "property", key: "twitter:title" },
  { attr: "property", key: "twitter:description" },
];

export const getMetaEl = ({ attr, key }) =>
  typeof document === "undefined"
    ? null
    : document.querySelector(`meta[${attr}="${key}"]`);

const SUMMIT_TITLE = "Postaway - Hospitality Summit";
const SUMMIT_DESCRIPTION =
  "Postaway - Hospitality Summit — September 27–29, 2026 · Breckenridge, Colorado. STR hosts and hospitality creators come together for three days of connection, learning, and collaboration in the Rockies.";

// Same live publishable key already shipped on the partner checkout page; the
// env var (build-time) takes precedence when set.
const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  "pk_live_51SDWk7GLCy9PVleCNU1Eow6f4yVZDhCTbLH0vNdUKbBqk05OsixJ2o17BfOwgXv5G69VsQe64z13tpB0nCtkwGrF00w3Ju6FYI";

// Lazily load Stripe.js only when the buyer reaches the payment step.
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

const TICKET_PRICE_LABEL = "$499";

const formatUsd = (cents) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

const TICKETS = [
  {
    id: "creator",
    label: "Creator Ticket",
    description: "Good for all 3 days",
  },
  {
    id: "host",
    label: "Host Ticket",
    description: "Good for all 3 days",
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SPEAKERS = [
  {
    name: "Jamie Kraus",
    handle: "@chestnuthillcabin",
    image: "/speaker-jamie-kraus-2026.jpeg",
  },
  {
    name: "Nikki & Hayden Hughes",
    handle: "@journey.more",
    image: "/speaker-nikki-hayden-hughes.jpeg",
  },
  {
    name: "Matt Sanderson",
    handle: "@striq.app",
    image: "/speaker-matt-sanderson.png",
  },
  {
    name: "Janel Gallucci",
    handle: "@sevensummershostacademy",
    image: "/speaker-janel.jpeg",
  },
  {
    name: "Katie Cline",
    handle: "@bykatiecline",
    image: "/speaker-katie-cline.png",
  },
  {
    name: "Steph Weber",
    handle: "@theweberco",
    image: "/speaker-steph-weber.png",
  },
  {
    name: "TJ Millonig",
    handle: "@thomasjmillonig",
    image: "/speaker-tj-millonig.jpeg",
  },
  {
    name: "Christina Franco",
    handle: "@rooted_homelife",
    image: "/speaker-christina.png",
  },
  {
    name: "Allie King",
    handle: "@somerled.designs",
    image: "/speaker-allie-king.png",
  },
  {
    name: "Dawn Asher",
    handle: "@theolivejar.co",
    image: "/speaker-dawn-asher.png",
  },
  {
    name: "Emma Brubaker",
    handle: "@eiffelemma",
    image: "/speaker-emma-brubaker.png",
  },
  {
    name: "Ella & Stephen Stewart",
    handle: "@the.backyardsocial",
    image: "/thebackyardsocial.jpeg",
  },
  {
    name: "Paige Hayes",
    handle: "@hayes_and_co",
    image: "/hayes_and_co.jpeg",
  },
  {
    name: "Melissa Stewart",
    handle: "@itelissastewart",
    image: "/itelissasteward.jpeg",
  },
  {
    name: "Umi Haft",
    handle: "@umihaftdesigns",
    image: "/speaker-umi-haft.jpg",
  },
  {
    name: "Brendan Dauenhauer",
    handle: "@buildwithbrendan",
    image: "/speaker-brendan-dauenhauer.png",
  },
  {
    name: "Brooke Lestikow",
    handle: "@d.bdens",
    image: "/speaker-brooke-lestikow.png",
  },
  {
    name: "Jamie Rogers",
    handle: "@captaincolorado",
    image: "/speaker-jamie-rogers.png",
  },
];

// Desktop layout: three even rows of 6.
const SPEAKER_ROWS = [SPEAKERS.slice(0, 6), SPEAKERS.slice(6, 12), SPEAKERS.slice(12)];

const SESSION_TYPES = [
  {
    label: "Main Sessions",
    text: "Creators and hosts will attend sessions within their own dedicated tracks, focused specifically on their side of the hospitality industry.",
  },
  {
    label: "Combined Sessions",
    text: "Hosts + creators come together to learn, connect, and explore topics that benefit both sides.",
  },
  {
    label: "Bonus Sessions",
    text: "Optional sessions open to everyone! Attend if the topic interests you or enjoy the extra time to network, explore Breckenridge, or recharge.",
  },
];

const SCHEDULE_DAYS = [
  {
    day: "Sunday",
    subtitle: "Arrival + Connection",
    items: [
      {
        time: "6:00–9:00 PM",
        title: "Meet & Greet (Optional)",
        note: "Come meet your fellow attendees, speakers, hosts, and creators before the summit officially kicks off!",
      },
    ],
  },
  {
    day: "Monday",
    items: [
      { time: "9:00 AM–12:00 PM", title: "Main Sessions" },
      { time: "12:00–2:00 PM", title: "Lunch Break" },
      { time: "2:00–4:00 PM", title: "Main Sessions" },
      { time: "4:00–5:00 PM", title: "Combined Host + Creator Session" },
      { time: "5:00–6:00 PM", title: "Bonus Session (Optional)" },
    ],
  },
  {
    day: "Tuesday",
    items: [
      { time: "8:00–9:00 AM", title: "Bonus Session (Optional)" },
      { time: "9:00 AM–12:00 PM", title: "Main Sessions" },
      { time: "12:00–2:00 PM", title: "Lunch Break" },
      { time: "2:00–4:00 PM", title: "Main Sessions" },
      { time: "4:00–5:00 PM", title: "Combined Host + Creator Session" },
    ],
  },
];

const scrollToTickets = (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById("tickets");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const SummitTickets = () => {
  // Phases: select (cards + form) → checkout (embedded Stripe) → confirmed.
  // "verifying" covers the return trip from Stripe while we check the session.
  const hasReturnSession =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("session_id");

  const [phase, setPhase] = useState(hasReturnSession ? "verifying" : "select");
  const [ticketType, setTicketType] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    propertyName: "",
    socialHandle: "",
    couponCode: "",
  });
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [applied, setApplied] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [notice, setNotice] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const sectionRef = useRef(null);

  const isHost = ticketType === "host";

  const errors = useMemo(() => {
    const next = {};
    if (!ticketType) next.ticketType = "Choose your ticket to continue.";
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_REGEX.test(form.email.trim()))
      next.email = "That email doesn't look right.";
    if (isHost && !form.propertyName.trim())
      next.propertyName = "Please enter your property name.";
    return next;
  }, [ticketType, form, isHost]);

  const showError = (field) =>
    (touched[field] || submitAttempted) && errors[field] ? errors[field] : "";

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const markTouched = (field) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // Verify the session when returning from Stripe, then clean the URL so a
  // refresh doesn't re-trigger verification.
  useEffect(() => {
    if (!hasReturnSession) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cleanUrl = () =>
      window.history.replaceState({}, "", window.location.pathname);

    fetch(`/api/summit/checkout?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => r.json())
      .then((data) => {
        cleanUrl();
        if (data?.ok && data.paid) {
          setConfirmation(data);
          setPhase("confirmed");
        } else {
          setNotice(
            "Your payment wasn't completed, so no charge was made. Feel free to try again below."
          );
          setPhase("select");
        }
      })
      .catch(() => {
        cleanUrl();
        setNotice(
          "We couldn't verify your payment just now. If you completed checkout, your confirmation email is on its way — otherwise, try again below."
        );
        setPhase("select");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the ticket section in view when arriving back from Stripe or when the
  // embedded payment form appears.
  useEffect(() => {
    if (phase === "verifying" || phase === "confirmed" || phase === "checkout") {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [phase]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitAttempted(true);
      setSubmitError("");
      if (Object.keys(errors).length) return;

      setSubmitting(true);
      try {
        const res = await fetch("/api/summit/checkout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ticketType,
            name: form.name.trim(),
            email: form.email.trim(),
            propertyName: form.propertyName.trim(),
            socialHandle: form.socialHandle.trim(),
            couponCode: form.couponCode.trim(),
          }),
        });
        const data = await res.json();
        if (data?.ok && data.clientSecret) {
          setNotice("");
          setCouponError("");
          setApplied(
            data.couponCode
              ? { amountTotal: data.amountTotal, couponCode: data.couponCode }
              : null
          );
          setClientSecret(data.clientSecret);
          setPhase("checkout");
        } else if (data?.error === "invalid_coupon") {
          setCouponOpen(true);
          setCouponError(
            "That coupon code isn't valid for summit tickets. Check the code and try again, or clear it to continue."
          );
        } else {
          setSubmitError("Something went wrong — please try again.");
        }
      } catch (err) {
        setSubmitError("Something went wrong — please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [errors, ticketType, form]
  );

  const handleEditDetails = () => {
    // Abandons the current Stripe session (it simply expires) and returns to
    // the form with everything still filled in.
    setClientSecret("");
    setPhase("select");
  };

  const selectedTicket = TICKETS.find((t) => t.id === ticketType);

  return (
    <div className="summit-tickets" id="tickets" ref={sectionRef}>
      <div className="summit-tickets-eyebrow">Get Your Ticket</div>

      {phase === "verifying" && (
        <div className="summit-verifying">
          <div className="summit-spinner" aria-hidden="true" />
          <div className="summit-verifying-text">Confirming your ticket…</div>
        </div>
      )}

      {phase === "confirmed" && (
        <div className="summit-confirmed" role="status">
          <div className="summit-confirmed-title">You're in!</div>
          <div className="summit-confirmed-ticket">
            {confirmation?.ticketType === "host"
              ? "Host Ticket (Good for all 3 days)"
              : "Creator Ticket (Good for all 3 days)"}
          </div>
          <div className="summit-confirmed-meta">
            September 27–29, 2026 · Mountain Thunder Lodge · Breckenridge,
            Colorado
          </div>
          <p className="summit-confirmed-note">
            A confirmation email is on its way
            {confirmation?.email ? (
              <>
                {" "}
                to <strong>{confirmation.email}</strong>
              </>
            ) : null}
            . We can't wait to welcome you to Breckenridge!
          </p>
        </div>
      )}

      {phase === "checkout" && (
        <div className="summit-checkout">
          <div className="summit-checkout-summary">
            <span className="summit-checkout-summary-text">
              {selectedTicket ? selectedTicket.label : "Summit Ticket"} × 1 ·{" "}
              {applied ? (
                <>
                  <s className="summit-checkout-strike">{TICKET_PRICE_LABEL}</s>{" "}
                  {formatUsd(applied.amountTotal)} with code {applied.couponCode}
                </>
              ) : (
                TICKET_PRICE_LABEL
              )}
              {form.email.trim() ? ` · ${form.email.trim()}` : ""}
            </span>
            <button
              type="button"
              className="summit-checkout-edit"
              onClick={handleEditDetails}
            >
              Edit details
            </button>
          </div>
          <div className="summit-checkout-frame">
            <EmbeddedCheckoutProvider
              key={clientSecret}
              stripe={getStripe()}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      )}

      {phase === "select" && (
        <form className="summit-ticket-form" onSubmit={handleSubmit} noValidate>
          {notice && <div className="summit-notice">{notice}</div>}

          <div
            className="summit-ticket-cards"
            role="radiogroup"
            aria-label="Ticket type"
          >
            {TICKETS.map((ticket) => (
              <label
                key={ticket.id}
                className={`summit-ticket-card${
                  ticketType === ticket.id ? " selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="ticketType"
                  value={ticket.id}
                  checked={ticketType === ticket.id}
                  onChange={() => {
                    setTicketType(ticket.id);
                    setTouched((prev) => ({ ...prev, ticketType: true }));
                  }}
                />
                <span className="summit-ticket-card-check" aria-hidden="true">
                  ✓
                </span>
                <span className="summit-ticket-card-label">{ticket.label}</span>
                <span className="summit-ticket-card-price">
                  {TICKET_PRICE_LABEL}
                </span>
                <span className="summit-ticket-card-desc">
                  {ticket.description}
                </span>
              </label>
            ))}
          </div>
          {showError("ticketType") && (
            <div className="summit-field-error summit-ticket-cards-error">
              {errors.ticketType}
            </div>
          )}
          <p className="summit-ticket-note">
            If you are both a host and creator, simply choose the category that
            most interests you.
          </p>

          <div className="summit-field">
            <label className="summit-field-label" htmlFor="summit-name">
              Name
            </label>
            <input
              id="summit-name"
              className={`summit-input${showError("name") ? " has-error" : ""}`}
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={setField("name")}
              onBlur={markTouched("name")}
              placeholder="Your full name"
            />
            {showError("name") && (
              <div className="summit-field-error">{errors.name}</div>
            )}
          </div>

          <div className="summit-field">
            <label className="summit-field-label" htmlFor="summit-email">
              Email address
            </label>
            <input
              id="summit-email"
              className={`summit-input${showError("email") ? " has-error" : ""}`}
              type="email"
              inputMode="email"
              autoComplete="email"
              value={form.email}
              onChange={setField("email")}
              onBlur={markTouched("email")}
              placeholder="you@example.com"
            />
            {showError("email") && (
              <div className="summit-field-error">{errors.email}</div>
            )}
          </div>

          <div
            className={`summit-field summit-field-property${
              isHost ? " visible" : ""
            }`}
            aria-hidden={!isHost}
          >
            <label className="summit-field-label" htmlFor="summit-property">
              Property name
            </label>
            <input
              id="summit-property"
              className={`summit-input${
                showError("propertyName") ? " has-error" : ""
              }`}
              type="text"
              value={form.propertyName}
              onChange={setField("propertyName")}
              onBlur={markTouched("propertyName")}
              placeholder="e.g. Chestnut Hill Cabin"
              tabIndex={isHost ? 0 : -1}
            />
            {showError("propertyName") && (
              <div className="summit-field-error">{errors.propertyName}</div>
            )}
          </div>

          <div className="summit-field">
            <label className="summit-field-label" htmlFor="summit-social">
              Social media handle{" "}
              <span className="summit-field-optional">(optional)</span>
            </label>
            <div className="summit-input-handle">
              <span className="summit-input-handle-prefix" aria-hidden="true">
                @
              </span>
              <input
                id="summit-social"
                className="summit-input"
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                value={form.socialHandle}
                onChange={setField("socialHandle")}
                placeholder="yourhandle"
              />
            </div>
          </div>

          {couponOpen || form.couponCode ? (
            <div className="summit-field">
              <label className="summit-field-label" htmlFor="summit-coupon">
                Coupon code{" "}
                <span className="summit-field-optional">(optional)</span>
              </label>
              <input
                id="summit-coupon"
                className={`summit-input summit-input-coupon${
                  couponError ? " has-error" : ""
                }`}
                type="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                value={form.couponCode}
                onChange={(e) => {
                  setCouponError("");
                  const value = e.target.value;
                  setForm((prev) => ({ ...prev, couponCode: value }));
                }}
                placeholder="Enter code"
              />
              {couponError && (
                <div className="summit-field-error">{couponError}</div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="summit-coupon-toggle"
              onClick={() => setCouponOpen(true)}
            >
              Have a coupon code?
            </button>
          )}

          {submitError && <div className="summit-notice error">{submitError}</div>}

          <button
            className="summit-btn primary summit-ticket-submit"
            type="submit"
            disabled={submitting}
          >
            {submitting
              ? "Preparing secure checkout…"
              : `Continue to payment — ${TICKET_PRICE_LABEL}`}
          </button>
          <div className="summit-trust-row">
            <span className="summit-trust-item">🔒 Secure checkout powered by Stripe</span>
            <span className="summit-trust-item">✉️ Instant email confirmation</span>
          </div>
        </form>
      )}
    </div>
  );
};

const SummitApplication = () => {
  // Stash the global title + meta tags from index.html, override them with
  // Summit-specific content while this page is mounted, and restore on unmount
  // so other routes keep their existing meta. Note: this only updates tags
  // client-side; for crawler-perfect per-page OG, a server-side render or
  // edge function would be required (global og:image already updated in
  // public/index.html).
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const originalTitle = document.title;
    const originals = SUMMIT_META_KEYS.map((tag) => {
      const el = getMetaEl(tag);
      return { tag, el, value: el ? el.getAttribute("content") : null };
    });

    document.title = SUMMIT_TITLE;
    originals.forEach(({ tag, el }) => {
      if (!el) return;
      const isTitleTag = tag.key.endsWith("title");
      el.setAttribute("content", isTitleTag ? SUMMIT_TITLE : SUMMIT_DESCRIPTION);
    });

    return () => {
      document.title = originalTitle;
      originals.forEach(({ el, value }) => {
        if (!el) return;
        if (value == null) {
          el.removeAttribute("content");
        } else {
          el.setAttribute("content", value);
        }
      });
    };
  }, []);

  const body = (
    <div className="summit-page">
      <div className="summit-intro">
        <img
          className="summit-hero-image"
          src="/summit-hero-aframe.jpeg"
          alt="A-frame cabin nestled in the forest"
        />
        <h1 className="summit-hero-title">Postaway Hospitality Summit</h1>
        <div className="summit-subtitle">STR Hosts / Creators</div>
        <div className="summit-event-meta">
          <span>September 27–29, 2026</span>
          <span className="summit-meta-dot" aria-hidden="true">·</span>
          <span>Breckenridge, Colorado</span>
        </div>
        <a
          className="summit-btn primary summit-hero-cta"
          href="#tickets"
          onClick={scrollToTickets}
        >
          Get Tickets · {TICKET_PRICE_LABEL}
        </a>
        <div className="summit-speakers">
          <div className="summit-speakers-eyebrow">Featured Speakers</div>
          <div className="summit-speakers-grid">
            {SPEAKER_ROWS.map((row, rowIndex) => (
              <div key={`speaker-row-${rowIndex}`} className="summit-speakers-row">
                {row.map((speaker) => (
                  <div key={speaker.name} className="summit-speaker">
                    <img
                      className="summit-speaker-image"
                      src={speaker.image}
                      alt={speaker.name}
                      loading="lazy"
                    />
                    <div className="summit-speaker-name">{speaker.name}</div>
                    {speaker.handle && (
                      <div className="summit-speaker-handle">{speaker.handle}</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <section className="summit-sponsor" aria-labelledby="summit-sponsor-heading">
          <h2 id="summit-sponsor-heading" className="summit-sponsor-eyebrow">
            Our Event Sponsor
          </h2>
          <div className="summit-sponsor-card">
            <a
              className="summit-sponsor-link"
              href="https://mountaincomfort.com/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Mountain Comfort Furnishings & Design (opens in a new tab)"
            >
              <img
                className="summit-sponsor-logo"
                src="/sponsors/mountain-comfort.webp"
                alt="Mountain Comfort Furnishings & Design"
                width="675"
                height="263"
                loading="lazy"
                decoding="async"
              />
            </a>
            <p className="summit-sponsor-note">
              Thank you for helping bring our hospitality community together.
            </p>
          </div>
        </section>
        <div className="summit-schedule">
          <div className="summit-schedule-eyebrow">Summit Schedule</div>
          <p className="summit-schedule-intro">
            The Postaway Summit is designed with a mix of track-specific
            learning, shared sessions, and optional bonus content.
          </p>
          <div className="summit-session-types">
            {SESSION_TYPES.map((type) => (
              <div key={type.label} className="summit-session-type">
                <div className="summit-session-type-label">{type.label}</div>
                <div className="summit-session-type-text">{type.text}</div>
              </div>
            ))}
          </div>
          <div className="summit-schedule-days">
            {SCHEDULE_DAYS.map((day) => (
              <div key={day.day} className="summit-schedule-day">
                <div className="summit-schedule-day-header">
                  <span className="summit-schedule-day-name">{day.day}</span>
                  {day.subtitle && (
                    <span className="summit-schedule-day-subtitle">
                      {day.subtitle}
                    </span>
                  )}
                </div>
                <div className="summit-schedule-items">
                  {day.items.map((item) => (
                    <div
                      key={`${day.day}-${item.time}-${item.title}`}
                      className="summit-schedule-item"
                    >
                      <div className="summit-schedule-time">{item.time}</div>
                      <div className="summit-schedule-detail">
                        <div className="summit-schedule-title">
                          {item.title}
                        </div>
                        {item.note && (
                          <div className="summit-schedule-note">
                            {item.note}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="summit-schedule-disclaimer">
            Specific session topics, speakers, and additional details will be
            announced separately. Schedule is subject to change.
          </p>
        </div>
        <p className="summit-description">
          Join hospitality hosts and creators in the Rockies for three days of
          connection, learning, and collaboration. Spaces are limited — grab
          your spot below and we'll see you in September.
        </p>
        <SummitTickets />
      </div>
      <SummitApplicationStyles />
    </div>
  );

  return <InfoPage below={body} disableStamps />;
};

// Exported so the Summit 2027 page (Summit2027.js) can share the same
// .summit-* styles and layer its own additions on top.
export const SummitApplicationStyles = () => (
  <style>{`
    body:has(.summit-sponsor) {
      min-width: 320px;
    }
    .summit-page:has(.summit-sponsor) {
      min-width: 0;
    }
    .summit-page {
      grid-column: 1 / -1;
      width: 100%;
      max-width: 760px;
      margin: 0 auto;
      padding: 24px 24px 56px;
      box-sizing: border-box;
    }
    @media (max-width: 768px) {
      .summit-page { padding: 12px 14px 40px; max-width: 100%; }
    }
    .summit-intro {
      text-align: center;
      margin-bottom: 28px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
    }
    @media (max-width: 768px) {
      .summit-intro { margin-bottom: 20px; gap: 8px; }
    }
    .summit-hero-image {
      display: block;
      width: 100%;
      aspect-ratio: 21 / 9;
      object-fit: cover;
      object-position: center;
      border-radius: 16px;
      margin: 0 0 16px;
      box-shadow: 0 8px 24px rgba(15, 8, 75, 0.14);
    }
    @media (max-width: 768px) {
      .summit-hero-image {
        aspect-ratio: 16 / 9;
        border-radius: 14px;
        margin-bottom: 12px;
        box-shadow: 0 4px 14px rgba(15, 8, 75, 0.16);
      }
    }
    .summit-hero-title {
      font-family: 'Sailors', serif;
      font-size: 44px;
      color: #0f084b;
      margin: 0;
      line-height: 1.05;
      letter-spacing: 0.01em;
      text-align: center;
    }
    @media (max-width: 768px) {
      .summit-hero-title { font-size: 30px; line-height: 1.1; }
    }
    @media (max-width: 380px) {
      .summit-hero-title { font-size: 26px; }
    }
    .summit-subtitle {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #dc1d08;
      margin: 0;
    }
    @media (max-width: 768px) {
      .summit-subtitle { font-size: 12px; letter-spacing: 0.16em; }
    }
    .summit-event-meta {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #0f084b;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;
    }
    @media (max-width: 768px) {
      .summit-event-meta { font-size: 12px; gap: 6px; }
    }
    .summit-meta-dot { color: #dc1d08; font-weight: 700; }
    .summit-speakers {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      width: 100%;
      margin-top: 4px;
    }
    @media (max-width: 768px) {
      .summit-speakers { gap: 12px; }
    }
    .summit-speakers-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #0f084b;
    }
    @media (max-width: 480px) {
      .summit-speakers-eyebrow { font-size: 11px; letter-spacing: 0.18em; }
    }
    .summit-speakers-grid {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 28px;
      width: 100%;
    }
    .summit-speakers-row {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      gap: 24px;
      flex-wrap: nowrap;
      width: 100%;
    }
    @media (max-width: 900px) {
      .summit-speakers-row {
        gap: 18px;
      }
    }
    @media (max-width: 768px) {
      /* On mobile, stack speakers in a single column so cards never sit
         awkwardly side-by-side on narrow screens. */
      .summit-speakers-grid {
        gap: 18px;
      }
      .summit-speakers-row {
        flex-direction: column;
        align-items: center;
        gap: 18px;
      }
    }
    .summit-speaker {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      max-width: 120px;
      flex: 0 1 120px;
    }
    @media (max-width: 768px) {
      .summit-speaker { max-width: 100%; flex: none; }
    }
    .summit-speaker-image {
      width: 96px;
      height: 96px;
      border-radius: 50%;
      object-fit: cover;
      object-position: center;
      border: 3px solid #ffffff;
      box-shadow: 0 4px 14px rgba(15, 8, 75, 0.18);
      background: #f4f3fb;
      display: block;
    }
    @media (max-width: 768px) {
      .summit-speaker-image {
        width: 100px;
        height: 100px;
      }
    }
    .summit-speaker-name {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #0f084b;
      text-align: center;
      line-height: 1.2;
    }
    .summit-speaker-handle {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: #dc1d08;
      text-align: center;
      line-height: 1.2;
    }
    .summit-sponsor {
      width: 100%;
      max-width: 640px;
      margin: 28px 0 12px;
      text-align: center;
    }
    .summit-sponsor-eyebrow {
      display: flex;
      align-items: center;
      gap: 16px;
      margin: 0 0 18px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.5;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #0f084b;
    }
    .summit-sponsor-eyebrow::before,
    .summit-sponsor-eyebrow::after {
      content: '';
      height: 1px;
      flex: 1;
      background: rgba(15, 8, 75, 0.14);
    }
    .summit-sponsor-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
      padding: 36px 28px 28px;
      border: 1px solid rgba(15, 8, 75, 0.1);
      border-radius: 16px;
      background: #fff;
      box-shadow: 0 4px 18px rgba(15, 8, 75, 0.04);
    }
    .summit-sponsor-link {
      display: block;
      width: 100%;
      max-width: 340px;
      border-radius: 4px;
    }
    .summit-sponsor-link:focus-visible {
      outline: 3px solid #0f084b;
      outline-offset: 6px;
    }
    .summit-sponsor-logo {
      display: block;
      width: 100%;
      max-width: 340px;
      height: auto;
      object-fit: contain;
    }
    .summit-page .summit-sponsor-note {
      max-width: 360px;
      margin: 0;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      line-height: 1.6;
      color: #535766;
    }
    @media (max-width: 480px) {
      .summit-sponsor { margin: 22px 0 10px; }
      .summit-sponsor-eyebrow { gap: 10px; font-size: 11px; letter-spacing: 0.14em; }
      .summit-sponsor-card { padding: 28px 20px 22px; gap: 20px; border-radius: 14px; }
    }
    .summit-schedule {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 18px;
      margin-top: 16px;
      text-align: left;
    }
    @media (max-width: 768px) {
      .summit-schedule { gap: 14px; margin-top: 10px; }
    }
    .summit-schedule-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #0f084b;
      text-align: center;
    }
    @media (max-width: 480px) {
      .summit-schedule-eyebrow { font-size: 11px; letter-spacing: 0.18em; }
    }
    .summit-schedule-intro {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      color: #353945;
      line-height: 1.55;
      max-width: 600px;
      margin: 0;
      text-align: center;
    }
    @media (max-width: 768px) {
      .summit-schedule-intro { font-size: 14px; }
    }
    .summit-session-types {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;
      max-width: 640px;
    }
    .summit-session-type {
      background: #f4f3fb;
      border-radius: 12px;
      padding: 14px 18px;
    }
    .summit-session-type-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #0f084b;
      margin-bottom: 4px;
    }
    .summit-session-type-text {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #353945;
      line-height: 1.5;
    }
    .summit-schedule-days {
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;
      max-width: 640px;
    }
    .summit-schedule-day-header {
      display: flex;
      align-items: baseline;
      gap: 10px;
      flex-wrap: wrap;
      border-bottom: 2px solid #0f084b;
      padding-bottom: 6px;
      margin-bottom: 10px;
    }
    .summit-schedule-day-name {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #0f084b;
    }
    .summit-schedule-day-subtitle {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: #dc1d08;
    }
    .summit-schedule-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .summit-schedule-item {
      display: flex;
      gap: 14px;
      align-items: baseline;
    }
    @media (max-width: 480px) {
      .summit-schedule-item { flex-direction: column; gap: 2px; }
    }
    .summit-schedule-time {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 700;
      color: #dc1d08;
      flex: 0 0 150px;
      white-space: nowrap;
    }
    @media (max-width: 480px) {
      /* flex-basis becomes height once the row stacks vertically */
      .summit-schedule-time { flex: none; }
    }
    .summit-schedule-detail { flex: 1; }
    .summit-schedule-title {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #0f084b;
      line-height: 1.4;
    }
    .summit-schedule-note {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      color: #353945;
      line-height: 1.5;
      margin-top: 2px;
    }
    .summit-schedule-disclaimer {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-style: italic;
      color: #777e90;
      line-height: 1.5;
      max-width: 600px;
      margin: 0;
      text-align: center;
    }
    .summit-description {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 16px;
      color: #353945;
      line-height: 1.55;
      max-width: 600px;
      margin: 0;
    }
    @media (max-width: 768px) {
      .summit-description { font-size: 14px; }
    }
    .summit-actions {
      display: flex;
      justify-content: center;
      margin-top: 8px;
    }
    .summit-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 600;
      text-decoration: none;
      border: 2px solid transparent;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 200px;
    }
    @media (max-width: 768px) {
      .summit-btn {
        width: 100%;
        min-height: 48px;
        min-width: 0;
        font-size: 16px;
        padding: 14px 20px;
      }
    }
    .summit-btn.primary {
      background: #dc1d08;
      color: #fff;
      border-color: #dc1d08;
    }
    .summit-btn.primary:hover {
      background: #b51706;
      border-color: #b51706;
    }
    .summit-btn.primary:disabled {
      background: #e58a80;
      border-color: #e58a80;
      cursor: default;
    }
    .summit-hero-cta {
      margin-top: 6px;
    }
    @media (max-width: 768px) {
      .summit-hero-cta { width: auto; min-width: 220px; }
    }
    .summit-tickets {
      width: 100%;
      max-width: 560px;
      margin: 8px auto 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      scroll-margin-top: 90px;
    }
    .summit-tickets-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #0f084b;
    }
    .summit-ticket-form {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .summit-ticket-cards {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      width: 100%;
    }
    @media (max-width: 480px) {
      .summit-ticket-cards { grid-template-columns: 1fr; }
    }
    .summit-ticket-card {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      background: #f4f3fb;
      border: 2px solid transparent;
      border-radius: 14px;
      padding: 16px 18px;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
    }
    .summit-ticket-card:hover {
      border-color: #c9c5e8;
    }
    .summit-ticket-card.selected {
      border-color: #0f084b;
      background: #ffffff;
      box-shadow: 0 4px 14px rgba(15, 8, 75, 0.12);
    }
    .summit-ticket-card input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      pointer-events: none;
    }
    .summit-ticket-card:has(input:focus-visible) {
      outline: 2px solid #dc1d08;
      outline-offset: 2px;
    }
    .summit-ticket-card-check {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #0f084b;
      color: #ffffff;
      font-size: 12px;
      line-height: 20px;
      text-align: center;
      opacity: 0;
      transform: scale(0.6);
      transition: opacity 0.15s, transform 0.15s;
    }
    .summit-ticket-card.selected .summit-ticket-card-check {
      opacity: 1;
      transform: scale(1);
    }
    .summit-ticket-card-label {
      font-size: 15px;
      font-weight: 700;
      color: #0f084b;
    }
    .summit-ticket-card-price {
      font-size: 24px;
      font-weight: 700;
      color: #dc1d08;
      line-height: 1.2;
    }
    .summit-ticket-card-desc {
      font-size: 13px;
      color: #353945;
    }
    .summit-ticket-note {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-style: italic;
      color: #777e90;
      line-height: 1.5;
      margin: -6px 0 0;
      text-align: center;
    }
    .summit-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      width: 100%;
      text-align: left;
    }
    .summit-field-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 700;
      color: #0f084b;
    }
    .summit-field-optional {
      font-weight: 400;
      color: #777e90;
    }
    .summit-input {
      width: 100%;
      box-sizing: border-box;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      color: #0f084b;
      background: #ffffff;
      border: 1.5px solid #d9d6ea;
      border-radius: 10px;
      padding: 12px 14px;
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      min-height: 48px;
    }
    @media (max-width: 768px) {
      /* 16px keeps iOS Safari from auto-zooming when the field is focused. */
      .summit-input { font-size: 16px; }
    }
    .summit-input::placeholder { color: #a5a1bd; }
    .summit-input:focus {
      border-color: #0f084b;
      box-shadow: 0 0 0 3px rgba(15, 8, 75, 0.1);
    }
    .summit-input.has-error {
      border-color: #dc1d08;
    }
    .summit-input-handle {
      position: relative;
      width: 100%;
    }
    .summit-input-handle-prefix {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 600;
      color: #777e90;
      pointer-events: none;
    }
    .summit-input-handle .summit-input {
      padding-left: 32px;
    }
    .summit-field-property {
      max-height: 0;
      opacity: 0;
      overflow: hidden;
      transition: max-height 0.25s ease, opacity 0.25s ease;
    }
    .summit-field-property.visible {
      max-height: 130px;
      opacity: 1;
    }
    .summit-field-error {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      color: #dc1d08;
    }
    .summit-ticket-cards-error {
      margin-top: -8px;
      text-align: center;
    }
    .summit-notice {
      width: 100%;
      box-sizing: border-box;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: #0f084b;
      background: #f4f3fb;
      border-radius: 10px;
      padding: 12px 16px;
      text-align: center;
    }
    .summit-notice.error {
      color: #b51706;
      background: #fdecea;
    }
    .summit-ticket-submit {
      width: 100%;
      min-height: 50px;
    }
    .summit-trust-row {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 8px 18px;
      margin-top: -4px;
    }
    .summit-trust-item {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      color: #777e90;
    }
    .summit-checkout {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .summit-checkout-summary {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      background: #f4f3fb;
      border-radius: 10px;
      padding: 10px 16px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      color: #0f084b;
    }
    .summit-checkout-summary-text {
      font-weight: 600;
      word-break: break-word;
    }
    .summit-coupon-toggle {
      background: none;
      border: none;
      padding: 0;
      align-self: flex-start;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #dc1d08;
      text-decoration: underline;
      cursor: pointer;
    }
    .summit-input-coupon {
      text-transform: uppercase;
    }
    .summit-input-coupon::placeholder {
      text-transform: none;
    }
    .summit-checkout-strike {
      color: #777e90;
      font-weight: 500;
    }
    .summit-checkout-edit {
      background: none;
      border: none;
      padding: 0;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #dc1d08;
      text-decoration: underline;
      cursor: pointer;
      white-space: nowrap;
    }
    .summit-checkout-frame {
      width: 100%;
      background: #ffffff;
      border-radius: 14px;
      overflow: hidden;
      min-height: 480px;
    }
    .summit-verifying {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      padding: 36px 0;
    }
    .summit-spinner {
      width: 28px;
      height: 28px;
      border: 3px solid #e5e2f3;
      border-top-color: #dc1d08;
      border-radius: 50%;
      animation: summit-spin 0.8s linear infinite;
    }
    @keyframes summit-spin {
      to { transform: rotate(360deg); }
    }
    .summit-verifying-text {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #353945;
    }
    .summit-confirmed {
      width: 100%;
      box-sizing: border-box;
      background: #f4f3fb;
      border-radius: 16px;
      padding: 32px 28px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }
    @media (max-width: 768px) {
      .summit-confirmed { padding: 24px 18px; }
    }
    .summit-confirmed-title {
      font-family: 'Sailors', serif;
      font-size: 38px;
      color: #0f084b;
      line-height: 1.05;
    }
    @media (max-width: 768px) {
      .summit-confirmed-title { font-size: 30px; }
    }
    .summit-confirmed-ticket {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 15px;
      font-weight: 700;
      color: #dc1d08;
    }
    .summit-confirmed-meta {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: #0f084b;
    }
    .summit-confirmed-note {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14px;
      color: #353945;
      line-height: 1.55;
      margin: 6px 0 0;
      max-width: 420px;
    }
  `}</style>
);

export default SummitApplication;
